import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

import Input from '@shared/components/forms/inputs/input/input.vue';
import EntitySelect from '@shared/components/forms/inputs/entity-select/entity-select.vue';
import DatePicker from '@shared/components/forms/inputs/datepicker/datepicker.vue';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { InvoiceValidation } from '@accounting/interfaces/invoice';
import { AccountingJournal } from '@accounting/interfaces/accounting-journal';
import { CenterSetting } from '@core/interfaces/center-setting';
import { AdvancedPrefixManagement } from '@accounting-integrations/interfaces/advanced-prefix-management';
import moment from 'moment';
import { DocumentNumberHelper } from '@accounting/helpers/DocumentNumberHelper';
import Switch from '@shared/components/forms/inputs/switch/switch.vue';

@Component({
  name: 'bpc-invoice-validation-form',
  components: {
    'bpc-input': Input,
    'bpc-date-picker': DatePicker,
    'bpc-switch': Switch,
    'bpc-entity-select': EntitySelect,
    ValidationProvider,
    ValidationObserver,
  },
})
export default class InvoiceValidationForm extends Vue {
  // PROPS
  @Prop(Object) entity;
  @Prop(Boolean) disabledFields: boolean;
  @Prop(Object) errors;
  @Prop({ type: Boolean, default: false }) readonly isBulk: boolean;
  @Prop(Array) readonly accountingJournalsType: string[];
  @Prop(Number) readonly initialAccountingJournalId: number;
  @Prop({ type: Boolean, default: false })
  readonly showProcessAllOptions: boolean;
  @Prop({ type: Number, default: 0 }) readonly totalEntities: number;
  @Prop({ type: Number, default: 0 }) readonly totalSelectedEntities: number;
  // DATA
  public selectConfigName: string[] = ['invoice-generation-select'];
  public permissionEntityNameToUse: string = 'invoices';
  loadingNextInvoiceNumberAndDate = false;
  nextValidDocumentSequence = null;
  nextInvoiceDate = null;
  lastSyncedDate = null;

  public formModel: InvoiceValidation = {
    ids: null,
    accounting_journal: null,
    invoice_date: null,
    due_date: null,
    document_sequence: 1,
    process_all: false,
    default_due_date: true,
  };

  public labelInputAttributes = {
    for: 'global__',
    cols: 12,
  };

  public errorCustom: AnyEntity = {};
  public showDateWarning: boolean = false;
  public showDateSyncWarning: boolean = false;
  public editingDocumentSequence: boolean = false;
  // COMPUTED

  get centerSetting(): CenterSetting {
    return this.$store.getters['centerSettings/centerSettings'];
  }

  get advancedPrefixManagement(): AdvancedPrefixManagement {
    return this.centerSetting?.advanced_prefix_management;
  }

  get example(): string {
    const number = this.formModel.document_sequence || 1;

    return DocumentNumberHelper.getDocumentNumber(
      number,
      this.formModel.accounting_journal,
      this.formModel.invoice_date,
    );
  }

  get fullPrefix(): string {
    if (this.formModel.accounting_journal) {
      return DocumentNumberHelper.getFullPrefix(
        this.formModel.accounting_journal,
        this.formModel.invoice_date,
      );
    }
    return '';
  }

  get fullPrefixAndZeros(): string {
    if (this.formModel.accounting_journal) {
      return DocumentNumberHelper.getFullPrefixAndZeros(
        this.formModel.document_sequence,
        this.formModel.accounting_journal,
        this.formModel.invoice_date,
      );
    }
    return '';
  }

  get showSequenceWarning(): boolean {
    return (
      this.formModel.document_sequence &&
      this.formModel.document_sequence < this.nextValidDocumentSequence
    );
  }

  get showSequenceGapInfo(): boolean {
    return (
      !this.showSequenceWarning &&
      this.formModel.document_sequence &&
      this.nextValidDocumentSequence &&
      Number(this.formModel.document_sequence) !== Number(this.nextValidDocumentSequence)
    );
  }

  get gapWhenEditingSequence(): number {
    return Number(this.formModel.document_sequence) - Number(this.nextValidDocumentSequence);
  }

  get prefix(): string {
    if (this.formModel.accounting_journal) {
      return DocumentNumberHelper.getPrefix(
        this.formModel.accounting_journal.document_number_prefix_structure,
        this.formModel.invoice_date,
      );
    }
    return '';
  }

  get accountingJournalSelectfilters() {
    return [
      {
        filterField: 'status',
        value: 'active',
      },
      {
        filterField: 'type',
        value: this.accountingJournalsType,
      },
    ];
  }

  get invoiceDate(): string | null {
    return this.formModel?.invoice_date;
  }

  get nextValidDateFormatted(): string {
    if (this.nextInvoiceDate) {
      return moment(this.nextInvoiceDate).format('DD/MM/YYYY');
    } else {
      return moment()
        .startOf('year')
        .format('DD/MM/YYYY');
    }
  }
  get lastSyncedDateFormatted(): string {
    if (this.lastSyncedDate) {
      return moment(this.lastSyncedDate).format('DD/MM/YYYY');
    }
    return null;
  }

  // METHODS
  onLoadedAccountingJournals(items: AccountingJournal[]) {
    let matchedAccountingJournal = null;
    if (this.initialAccountingJournalId) {
      matchedAccountingJournal = items.find(
        accountingJournal => accountingJournal.id === this.initialAccountingJournalId,
      );
    }
    if (!matchedAccountingJournal && items && items.length === 1) {
      matchedAccountingJournal = items[0];
    }

    if (matchedAccountingJournal) {
      this.onChangeAccountingJournal(matchedAccountingJournal);
    }
  }

  onCleanErrors(input) {
    this.errorCustom[input] = null;
  }

  // Format the recieved errors from backend and set it in the correct way for frontend
  setErrors() {
    if (this.errors) {
      this.errorCustom = Object.keys(this.errors).reduce((res, key) => {
        res[key] = this.errors[key][0] || null;
        return res;
      }, {});
    }
  }

  public cleanFormModel() {
    this.errorCustom = {};
    this.formModel.ids = null;
    this.nextValidDocumentSequence = null;
    this.formModel.document_sequence = 1;
  }

  async onChangeAccountingJournal(accountingJournal: AccountingJournal) {
    if (accountingJournal) {
      this.formModel.accounting_journal = accountingJournal;
      this.nextValidDocumentSequence = null;
      this.formModel.document_sequence = 1;
      this.errorCustom = {};
      await this.setInitialInvoiceNumberAndDate();
    }
  }

  async getNextInvoiceNumber(
    accounting_journal_id: number,
    invoice_date: string,
  ): Promise<{
    invoice_number: number;
    invoice_date: string;
    invoice_last_synced_date: string | null;
  }> {
    const path = `${this.$bpcApiRouteHelper.getEntityPath('invoice')}/last-number-and-date`;
    const res = await this.$bpcApi.post(path, { accounting_journal_id, invoice_date }).toPromise();

    const lastNumber = res?.data?.invoice_number || 0;
    const lastDate = res?.data?.invoice_date || moment(invoice_date).format('YYYY-MM-DD');
    const lastSyncDate = res?.data?.invoice_last_synced_date || null;

    return {
      invoice_number: lastNumber + 1,
      invoice_date: lastDate,
      invoice_last_synced_date: lastSyncDate,
    };
  }

  async setInitialInvoiceNumberAndDate() {
    this.loadingNextInvoiceNumberAndDate = true;
    this.clearInvoiceNumber();
    const numberAndDate = await this.getNextInvoiceNumber(
      this.formModel.accounting_journal.id,
      moment().format('YYYY-MM-DD'),
    );
    this.nextValidDocumentSequence = numberAndDate.invoice_number;
    this.nextInvoiceDate = numberAndDate.invoice_date;
    // Next line commented to not initilize the invoice date and let the user choose it instead
    // The logic is kept though, in case we have to go back to the previous usage
    // this.formModel.invoice_date = numberAndDate.invoice_date;
    this.formModel.document_sequence = numberAndDate.invoice_number;
    this.loadingNextInvoiceNumberAndDate = false;
  }

  async setNextInvoiceNumberAndDate() {
    this.loadingNextInvoiceNumberAndDate = true;
    this.clearInvoiceNumber();
    this.$emit('loading-next-invoice-number', this.loadingNextInvoiceNumberAndDate);
    const numberAndDate = await this.getNextInvoiceNumber(
      this.formModel.accounting_journal.id,
      this.formModel.invoice_date,
    );
    this.nextValidDocumentSequence = numberAndDate.invoice_number;
    this.nextInvoiceDate = numberAndDate.invoice_date;
    this.lastSyncedDate = numberAndDate.invoice_last_synced_date;
    this.formModel.document_sequence = numberAndDate.invoice_number;
    this.loadingNextInvoiceNumberAndDate = false;
    this.$emit('loading-next-invoice-number', this.loadingNextInvoiceNumberAndDate);
  }

  clearInvoiceNumber() {
    this.nextValidDocumentSequence = null;
    this.nextInvoiceDate = null;
    this.lastSyncedDate = null;
    this.formModel.document_sequence = 1;
  }

  async initFormModel() {
    if (this.entity?.accounting_journal_id) {
      const getAccountingJournalRoute = `${this.$bpcApiRouteHelper.getEntityPath(
        'accounting-journals',
      )}/${this.entity.accounting_journal_id}`;

      this.formModel.ids = [this.entity.id];
      this.$bpcApi.get(getAccountingJournalRoute).subscribe(
        res => {
          const accountingJournal = res.data as AccountingJournal;

          this.formModel.accounting_journal = accountingJournal;
          this.setInitialInvoiceNumberAndDate();
        },
        error => {
          this.$bpcMessage.showErrorMessage({
            text: 'global.generic.error_form_action',
          });
        },
      );
    }
  }

  onEditDocumentSequence() {
    this.editingDocumentSequence = !this.editingDocumentSequence;
  }
  onCancelSequenceEdition() {
    this.onResetSequenceEdition();
    this.editingDocumentSequence = false;
  }
  onResetSequenceEdition() {
    this.formModel.document_sequence = this.nextValidDocumentSequence;
  }

  //LIFECYCLE

  destroy() {
    this.cleanFormModel();
  }

  // WATCHERS
  @Watch('errors', { immediate: true, deep: true })
  onErrorsChange() {
    this.setErrors();
  }

  @Watch('entity', { immediate: true, deep: true })
  onInvoiceChange() {
    this.initFormModel();
  }

  @Watch('invoiceDate')
  async onInvoiceDateChange(val) {
    this.showDateWarning = false;
    this.showDateSyncWarning = false;
    if (val) {
      await this.setNextInvoiceNumberAndDate();
      if (this.nextInvoiceDate) {
        const validDate = moment(this.nextInvoiceDate);
        const newDate = moment(val);
        if (validDate.isAfter(newDate)) {
          this.showDateWarning = true;
        }
      }

      if (this.lastSyncedDate) {
        const validSyncDate = moment(this.lastSyncedDate).startOf('day');
        const newDate = moment(val).startOf('day');
        if (validSyncDate.isAfter(newDate)) {
          this.showDateSyncWarning = true;
        }
      }

      let dueDays = this.formModel.accounting_journal.number_of_days_for_due_dates;

      if (!this.isBulk) {
        if (this.entity?.contact?.due_date_length) {
          dueDays = this.entity?.contact?.due_date_length;
        }
      }

      this.formModel.due_date = moment(val)
        .add(dueDays, 'days')
        .format('YYYY-MM-DD');
    }
  }
}
